import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _omit from 'lodash/omit';
import _get from 'lodash/get';
import {
  FilterGroupingEnum,
  Site,
  SiteCreateInputObject,
  SiteUpdateInputObject,
  useCountriesQuery,
  User,
  UserRoleEnum,
  UserScopeNameEnum,
  useSectorsSearchListLazyQuery,
  useSiteCreateMutation,
  useSiteDestroyMutation,
  useSiteQuery,
  useSiteUpdateMutation,
  useUsersSearchListLazyQuery,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import getFullName from '@lib/utils/getFullName';
import { Values } from '@lib/interfaces/form';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { APP_URLS } from 'constants/urls';
import { getUsersLoadOptions } from 'utils/fetch/searchLists/usersSearchList';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { getSectorsLoadOptions } from 'utils/fetch/searchLists/sectorsSearchList';
import replaceNullsWithUndefined from '@lib/utils/replaceNullsWithUndefined';
import getSiteDefaultValuesFromQueryData from 'routes/Sites/utils/getSiteDefaultValuesFromQueryData';
import useUsersQueryHook from 'utils/fetch/hooks/useUsersQueryHook';
import { GetUrlParams } from '@lib/enums/urls';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import { SiteViewMode } from 'routes/Sites/enums';
import getSitePreparedDataFromValues from './getSitePreparedDataFromValues';

interface UseActionsProps {
  formRef: FormRefType;
  id: string;
  useModalManagementResult: UseModalManagementResult;
  viewMode: SiteViewMode;
}

export interface UseSiteActionsResult {
  siteQuery: ReturnType<typeof useSiteQuery>;
  createMutationResult: ReturnType<typeof useSiteCreateMutation>[1];
  updateMutationResult: ReturnType<typeof useSiteUpdateMutation>[1];
  countriesCodes: string[];
  onConfirmedDelete: () => void;
  onCreate: (values: Values) => void;
  onCreateUserCallback: (newUser?: User | null) => void;
  onEdit: (values: Values) => void;
  onOpenSectorPage: () => void;
  sectorsLoadOptions: ReturnType<typeof getSectorsLoadOptions>;
  usersLoadOptions: ReturnType<typeof getUsersLoadOptions>;
  usersQueryHookResult: ReturnType<typeof useUsersQueryHook>;
  responsiblePersonLoadOptions: ReturnType<typeof getUsersLoadOptions>;
  onViewCodeOfConduct: () => void;
  onEditCodeOfConduct: () => void;
  onConfirmedDeleteCodeOfConduct: () => void;
}

export default function useSiteActions(
  props: UseActionsProps,
): UseSiteActionsResult {
  const { id, useModalManagementResult, formRef, viewMode } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { closeAllModals } = useModalManagementResult;
  const closeSiteDeleteModal = () => closeAllModals();
  const closeUserFormModal = () => closeAllModals();
  const closeCodeOfConductDeleteModal = () => closeAllModals();

  const siteQuery = useSiteQuery({
    skip: !id,
    variables: {
      findBy: { id: id || '' },
    },
  });

  const [fetchUsersSearchListLazyQuery] = useUsersSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const usersLoadOptions = getUsersLoadOptions(
    fetchUsersSearchListLazyQuery,
    { name: UserScopeNameEnum.Active },
    [
      {
        role: {
          grouping: FilterGroupingEnum.Or,
          predicate: { eq: UserRoleEnum.User },
        },
      },
    ],
  );
  const responsiblePersonLoadOptions = getUsersLoadOptions(
    fetchUsersSearchListLazyQuery,
    {
      name: UserScopeNameEnum.Active,
    },
  );

  const [fetchSectorsSearchListLazyQuery] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const sectorsLoadOptions = getSectorsLoadOptions(
    fetchSectorsSearchListLazyQuery,
  );

  const countriesQuery = useCountriesQuery({
    fetchPolicy: 'cache-first',
  });
  const countries = _get(countriesQuery, 'data.data', []);
  const countriesCodes = countries.map(({ code }) => code.toLowerCase());

  // Users for view
  const usersQueryHookResult = useUsersQueryHook({
    paginationParams: {
      [GetUrlParams.Page]: '1',
      [GetUrlParams.PerPage]: '4',
    },
    skip: true,
    skipGetAllIds: viewMode !== SiteViewMode.Edit,
    skipGetCounters: true,
    additionalFilters: [
      {
        memberAssignedSitesId: {
          grouping: FilterGroupingEnum.Or,
          predicate: { eq: id },
        },
      },
    ],
    includeTeamAccesses: true,
  });

  const [createMutation, createMutationResult] = useSiteCreateMutation({
    onCompleted: (response) => {
      const { id: newId } = { ...response.data } as Site;
      if (newId) {
        navigate(
          APP_URLS.dashboard.sites.view.getDynamicPath({
            pathParts: {
              id: newId,
            },
          }),
        );
      } else {
        navigate(APP_URLS.dashboard.sites.index.getPathWithQuery());
      }
      toast({ content: t('create-site-success') });
    },
    onError: (error) => showGraphQLErrors(error, t),
  });

  const [updateMutation, updateMutationResult] = useSiteUpdateMutation({
    onCompleted: () => {
      navigate(
        APP_URLS.dashboard.sites.view.getDynamicPath({
          pathParts: {
            id,
          },
        }),
      );
      toast({ content: t('update-site-success') });
    },
    onError: (error) => showGraphQLErrors(error, t),
  });

  const [destroyMutation] = useSiteDestroyMutation({
    onCompleted: () => {
      closeSiteDeleteModal();
      navigate(APP_URLS.dashboard.sites.index.getPathWithQuery());
    },
    onError: (error) => showGraphQLErrors(error, t),
  });

  const siteData = _get(siteQuery, 'data.site') as Site;

  const onCreate = (values: Values) => {
    const attributes = getSitePreparedDataFromValues(
      values,
      {},
    ) as SiteCreateInputObject;
    createMutation({
      variables: {
        attributes,
      },
    });
  };

  const onEdit = (values: Values) => {
    if (id) {
      updateMutation({
        variables: {
          id,
          attributes: _omit(
            getSitePreparedDataFromValues(
              values,
              replaceNullsWithUndefined(
                getSiteDefaultValuesFromQueryData({
                  data: siteData as Values,
                  assigneeIds: usersQueryHookResult.allIds,
                  t,
                }),
              ),
            ),
            'code',
          ) as SiteUpdateInputObject,
        },
      });
    }
  };

  const onConfirmedDelete = () => {
    closeSiteDeleteModal();
    toast({
      content: t('destroy-site-success'),
      closeCallback: () => {
        if (id) {
          destroyMutation({
            variables: { id },
          });
        }
      },
    });
  };

  const onDestroyCodeOfConductHandler = () => {
    if (id) {
      updateMutation({
        variables: { id, attributes: { conductCodeSections: [] } },
      });
    }
  };

  const onCreateUserCallback = (newUser?: User | null) => {
    const newUserId = newUser?.id;
    const newUserFullName = getFullName(newUser);
    if (newUserId && formRef?.current?.setValue) {
      formRef.current.setValue(
        'userId',
        {
          value: newUserId,
          label: newUserFullName,
          data: newUser,
        },
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
    closeUserFormModal();
  };

  const onOpenSectorPage = () => {
    const sectorId = _get(siteData, 'sector.id');
    if (sectorId) {
      navigate(
        APP_URLS.dashboard.sectors.view.getDynamicPath({
          pathParts: { id: sectorId },
        }),
      );
    }
  };

  const onViewCodeOfConduct = () =>
    navigate(
      APP_URLS.dashboard.sites.viewCodeOfConduct.getDynamicPath({
        pathParts: {
          id: `${id}`,
        },
      }),
    );

  const onEditCodeOfConduct = () =>
    navigate(
      APP_URLS.dashboard.sites.editCodeOfConduct.getDynamicPath({
        pathParts: {
          id: `${id}`,
        },
      }),
    );

  const onConfirmedDeleteCodeOfConduct = () => {
    closeCodeOfConductDeleteModal();
    toast({
      content: t('destroy-code-of-conduct-success'),
      closeCallback: onDestroyCodeOfConductHandler,
    });
  };

  return {
    countriesCodes,
    createMutationResult,
    onConfirmedDelete,
    onConfirmedDeleteCodeOfConduct,
    onCreate,
    onCreateUserCallback,
    onEdit,
    onEditCodeOfConduct,
    onOpenSectorPage,
    onViewCodeOfConduct,
    responsiblePersonLoadOptions,
    sectorsLoadOptions,
    siteQuery,
    updateMutationResult,
    usersLoadOptions,
    usersQueryHookResult,
  };
}
